/**
 * EVO — yon menyu.
 *
 * Bu fayl RUXSAT HAQIDA HECH NARSA BILMAYDI. U menyu ma'lumotini
 * (`config/menu.ts`) va `can()` funksiyasini oladi, filtrlashni esa
 * `filterMenu` bajaradi. Sabab: qoidalar bitta joyda tursin,
 * komponent faqat chizsin.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN GURUHLAR YIG'ILADI                                            ║
 * ║                                                                          ║
 * ║ Avval barcha guruhlar ochiq turardi: admin uchun bu 9 sarlavha va 31     ║
 * ║ band, ya'ni 40 qatorli, skroll qilinadigan ro'yxat. Egasining birinchi   ║
 * ║ mulohazasi aynan shu bo'ldi — «bo'limlar uzun qator bo'lib ketgan».      ║
 * ║                                                                          ║
 * ║ Muammo uzunlikda emas, TANLASHDA: 40 tadan bittasini topish uchun ko'z   ║
 * ║ butun ro'yxatni o'qishi kerak. Yig'ilgan holatda esa 9 ta sarlavha       ║
 * ║ ko'rinadi va tanlov ikki qadamga bo'linadi — har qadamda 9 dan bitta.    ║
 * ║ Bu ancha tez, chunki bo'lim nomi («Ombor», «Savdo») kundalik so'z, band  ║
 * ║ nomi esa aniqlashtiruvchi.                                               ║
 * ║                                                                          ║
 * ║ FAOL guruh HAR DOIM ochiq: foydalanuvchi qayerda turganini ko'rishi      ║
 * ║ kerak, va ekrandan ekranga o'tish eng ko'p uchraydigan harakat —         ║
 * ║ «Qoldiq» dan «Kirim» ga o'tish uchun menyuni qayta ochish shart emas.    ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { filterMenu, findActiveMenuItem, MENU, type MenuGroup } from '../../config/menu';
import { useCan } from '../../lib/auth';
import { useT } from '../../lib/i18n-context';
import { AlightMark } from '../brand/alight-logo';
import { Chevron, NavIcon } from './nav-icons';

/**
 * Qaysi guruhlar QO'LDA ochilgani.
 *
 * `localStorage` da saqlanadi: kunlik ish bir necha bo'lim orasida kechadi
 * (ombor + savdo), va har sahifa yangilanganda ularni qayta ochish —
 * kuniga o'nlab ortiqcha bosish.
 *
 * Faol guruh bu ro'yxatga KIRMAYDI: u boshqa qoida bilan, avtomatik ochiladi.
 * Ikkalasini bitta holatga qo'shsak, foydalanuvchi faol guruhni yopmoqchi
 * bo'lganda u darhol qayta ochilardi.
 */
const STORAGE_KEY = 'evo.nav.open';

function readOpen(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    // Buzilgan qiymat yoki `localStorage` yopiq (private rejim) — menyu
    // baribir ishlashi kerak, shuning uchun jimgina bo'sh holatga tushamiz.
    return new Set();
  }
}

/**
 * Yon menyu.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ TELEFONDA — TORTMA (drawer), USTUN EMAS                                  ║
 * ║                                                                          ║
 * ║ Menyu `w-56` (224 px) va u `shrink-0` edi, ya'ni HAR QANDAY ekranda      ║
 * ║ 224 px egallardi. 390 px li telefonda kontentga 166 px qolardi va ERP    ║
 * ║ amalda ishlatib bo'lmasdi: raqamlar kesilib, jadval ustunlari            ║
 * ║ ko'rinmasdi. Jonli o'lchandi — bosh sahifadagi «$63 095.00» ekrandan     ║
 * ║ 78 px chiqib ketgan va JIMGINA kesilgan edi (sahifa gorizontal           ║
 * ║ aylanmaydi, ya'ni foydalanuvchi uni surib ham ko'rolmasdi).              ║
 * ║                                                                          ║
 * ║ Pastki tab paneli (usta va dala ilovalaridagidek) BU YERDA to'g'ri       ║
 * ║ kelmaydi: u yerda 4–5 ekran bor, ERP da esa 9 guruh va 40 dan ortiq      ║
 * ║ band. Tortma esa butun daraxtni saqlaydi va u faqat kerak bo'lganda      ║
 * ║ joy egallaydi.                                                           ║
 * ║                                                                          ║
 * ║ `lg` dan boshlab menyu odatdagidek — statik ustun. Ofisdagi odam uchun   ║
 * ║ hech narsa o'zgarmadi va o'zgarmasligi ham kerak: u kuniga o'nlab marta  ║
 * ║ menyuga murojaat qiladi va uni har safar ochish qo'shimcha bosish        ║
 * ║ bo'lardi.                                                                ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export function Sidebar({
  mobileOpen = false,
  onClose,
}: {
  readonly mobileOpen?: boolean;
  readonly onClose?: () => void;
} = {}): ReactNode {
  const t = useT();
  const can = useCan();

  // Ruxsatlar o'zgarmaguncha qayta hisoblanmaydi (rol almashishi juda kam hodisa).
  const groups = useMemo(() => filterMenu(MENU, can), [can]);

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [manuallyOpen, setManuallyOpen] = useState<ReadonlySet<string>>(readOpen);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...manuallyOpen]));
    } catch {
      /* private rejim — saqlash imkonsiz, menyu baribir ishlaydi */
    }
  }, [manuallyOpen]);

  /*
   * Joriy manzil qaysi guruhga tegishli.
   *
   * Mantiq `config/menu.ts` da, chunki yuqori panel ham AYNAN shundan
   * sahifa sarlavhasini oladi. Ikki joyda ikki nusxa yozilsa, ular bir
   * kun ajralib ketadi va menyuda «Ombor» yoritilgan turib sarlavhada
   * «Savdo» yozilishi mumkin.
   */
  const activeGroupId = useMemo(
    () => findActiveMenuItem(groups, pathname)?.group.id ?? null,
    [groups, pathname],
  );

  const toggle = (id: string): void =>
    setManuallyOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      {/*
        Fon qoraytmasi — FAQAT telefonda va faqat ochiq holatda.

        `aria-hidden`: u ma'lumot tashimaydi. Yopish uchun bosiladi, lekin
        klaviatura foydalanuvchisi uchun Escape bor (`app-shell.tsx`) —
        shuning uchun bu `button` emas, `div`: ekran o'quvchisi ro'yxatida
        maqsadsiz element paydo bo'lmaydi.
      */}
      {mobileOpen && (
        <div
          aria-hidden="true"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      )}

      <nav
        aria-label={t('nav.menu')}
        /*
         * Telefonda — `fixed` tortma; `lg` dan — odatdagi ustun.
         *
         * `translate-x` ishlatiladi, `hidden` emas: `hidden` da ochilish
         * animatsiyasiz sakrab chiqadi va foydalanuvchi menyu QAYERDAN
         * kelganini ko'rmaydi. Siljish esa yo'nalishni ko'rsatadi va
         * yopish uchun qayerga bosish kerakligini o'zi aytadi.
         *
         * `lg:translate-x-0` MAJBURIY: usiz katta ekranda menyu yopiq
         * holatda chap tomonga surilib, umuman ko'rinmay qolardi.
         */
        className={`fixed inset-y-0 left-0 z-40 evo-chrome flex w-72 flex-col border-r border-chrome-line transition-transform duration-200 motion-reduce:transition-none lg:static lg:z-auto lg:w-66 lg:shrink-0 lg:translate-x-0 lg:shadow-none ${
          mobileOpen ? 'evo-raised translate-x-0' : '-translate-x-full'
        }`}
      >
      {/*
       * Brend sarlavhasi. Menyudan tashqarida, ya'ni skroll qilinmaydi —
       * dastur nomi doim ko'rinib turishi kerak, ayniqsa bir necha tizim
       * bilan ishlaydigan odam uchun (hozircha 1C ham ochiq turadi).
       */}
      {/*
       * ╔══════════════════════════════════════════════════════════════════════╗
       * ║ FAQAT EMBLEMA — YOZUVSIZ (egasining qarori, 2026-08-15)              ║
       * ║                                                                      ║
       * ║ Bu yerda ilgari nom YOZUV bo'lib turardi: avval «EVO» + «ALIGHT      ║
       * ║ ELECTRICAL», keyin «Alight» + «ELECTRICAL». Egasi ikkalasini ham     ║
       * ║ olib tashlashni so'radi — belgi o'zi yetarli.                        ║
       * ║                                                                      ║
       * ║ Va u haqiqatan yetarli: logo Alight'niki, uni shu kompaniyada        ║
       * ║ ishlaydigan odam bir qarashda taniydi. Yozuv esa har til             ║
       * ║ almashganda joy egallardi va menyu sarlavhasini ikki qatorga         ║
       * ║ cho'zardi — 16 ta bo'lim turgan panelda bu bekor sarflangan          ║
       * ║ balandlik.                                                           ║
       * ║                                                                      ║
       * ║ Nom butunlay yo'qolmaydi: u brauzer tabida (`index.html` →           ║
       * ║ `<title>`) va kirish ekranida turadi.                                ║
       * ╚══════════════════════════════════════════════════════════════════════╝
       */}
      <div className="flex h-16 shrink-0 items-center border-b border-chrome-line px-4">
        {/*
         * ┌──────────────────────────────────────────────────────────────────┐
         * │ BELGI TAGIDA TO'Q TAGLIK — ENDI KADR, MAJBURIYAT EMAS            │
         * │                                                                  │
         * │ Korpus jade bo'lgan paytda bu taglik SHART edi: chaqmoq #24AB6B  │
         * │ panel #0B3D29 ustida 4.15 da turardi va uzoqdan qaralganda       │
         * │ belgi panelga singib ketardi.                                    │
         * │                                                                  │
         * │ Korpus grafitga o'tgach farq 6.03 ga chiqdi, ya'ni taglikning    │
         * │ ASL SABABI YO'Q. U olib tashlanmadi, chunki logo faylining       │
         * │ o'zida ham chaqmoq to'q kvadrat ustida turadi (`favicon.svg` da  │
         * │ ham shunday) — taglik o'sha kadrni takrorlaydi va belgi          │
         * │ menyuning qolgan qismidan alohida buyum bo'lib ko'rinadi.        │
         * │                                                                  │
         * │ Yozuv olib tashlangach taglik BIROZ KATTALASHDI (9 → 10): belgi  │
         * │ endi sarlavhaning yagona egasi va oldingi o'lchamda u panelning  │
         * │ chap burchagida yo'qolib qolardi.                                │
         * └──────────────────────────────────────────────────────────────────┘
         */}
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-chrome-deep">
          <AlightMark className="size-7" />
        </span>
      </div>

      {/*
       * `py-2` emas, `py-3` va `px-2.5`: bandlar orasidagi nafas
       * bosish nishonini kattalashtiradi. Omborchi menyuni sichqoncha
       * bilan emas, ko'pincha barmoq bilan bosadi (planshet) va 32 px
       * balandlikdagi qator xato bosishga olib kelardi.
       */}
      <div className="flex-1 overflow-y-auto px-2.5 py-3">
        {groups.map((group) => (
          <NavGroup
            key={group.id}
            group={group}
            /*
             * Faol guruh HAR DOIM ochiq — hatto qo'lda yopilgan bo'lsa ham.
             * Aks holda foydalanuvchi bosgan bandning o'zi ko'zdan yo'qolardi.
             */
            open={group.id === activeGroupId || manuallyOpen.has(group.id)}
            active={group.id === activeGroupId}
            onToggle={() => toggle(group.id)}
          />
        ))}
      </div>
      </nav>
    </>
  );
}

function NavGroup({
  group,
  open,
  active,
  onToggle,
}: {
  readonly group: MenuGroup;
  readonly open: boolean;
  readonly active: boolean;
  readonly onToggle: () => void;
}): ReactNode {
  const t = useT();

  /*
   * BITTA BANDLI GURUH — to'g'ridan-to'g'ri havola.
   *
   * Yig'iladigan guruhning ma'nosi — ko'p banddan bittasini tanlash. Band
   * bitta bo'lsa, ochish qadami hech narsa bermaydi: foydalanuvchi bir
   * bosishni ikkiga aylantirasiz va u har safar bir xil natijani ko'radi.
   *
   * Bu holat ruxsatga qarab O'ZGARADI: «Moliya» adminda 1 band («Kassa»),
   * chunki qolgan to'rttasi hali yozilmagan. Ekranlar yozilgach guruh
   * o'z-o'zidan yig'iladigan bo'ladi — kod o'zgarmaydi.
   *
   * Sarlavha sifatida BAND nomi olinadi, guruh nomi emas: bosilganda
   * ochiladigan ekranning nomi turishi kerak. «Moliya» bosib «Kassa
   * hisoblari» ga tushish chalg'itadi.
   */
  const only = group.items.length === 1 ? group.items[0]! : null;
  if (only !== null) {
    return (
      <Link
        to={only.route!}
        className="mb-0.5 flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-base font-medium text-chrome-ink transition-colors motion-reduce:transition-none hover:bg-chrome-2"
        /*
         * FAOL band — BRENDGA bo'yaladi, faqat qalinlashmaydi.
         *
         * Ilgari farq qalin shrift + och fon edi va qorong'i mavzuda u
         * deyarli ko'rinmasdi: `bg-brand-soft` u yerda juda to'q va matn
         * bilan kontrasti past. Endi uch signal birga ishlaydi — fon,
         * matn rangi va nishon rangi — ya'ni bittasi yo'qolsa ham
         * qolgani ko'rinadi.
         */
        activeProps={{ className: 'bg-chrome-2! text-white! font-semibold' }}
        activeOptions={{ exact: only.route === '/' }}
      >
        <NavIcon name={group.icon} className="h-5 w-5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{t(only.labelKey)}</span>
      </Link>
    );
  }

  return (
    <div className="mb-0.5">
      {/*
       * GURUH sarlavhasi — faol bo'lganda BRAND rangida.
       *
       * Ilgari u faqat qalinlashardi (`font-semibold text-ink`) va bu
       * yig'ilgan holatda deyarli sezilmasdi: guruh yopiq turganda
       * foydalanuvchi qaysi bo'limda ekanini BILISHNING yagona yo'li shu
       * sarlavha edi, qalinlik esa yonidagi sarlavhalar bilan bir qarashda
       * farq qilmaydi. Fon bo'yash RAD ETILDI — u ichkaridagi faol bandning
       * foni bilan raqobatlashardi va ikkalasi bir xil vaznda ko'rinardi.
       */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-base transition-colors motion-reduce:transition-none ${
          active ? 'evo-chrome-active font-semibold' : 'font-medium text-chrome-ink hover:bg-chrome-2'
        }`}
      >
        <NavIcon name={group.icon} className={`h-5 w-5 shrink-0 ${active ? 'text-chrome-accent' : 'text-chrome-muted'}`} />
        <span className="min-w-0 flex-1 truncate">{t(group.labelKey)}</span>
        <Chevron open={open} />
      </button>

      {open && (
        <ul
          /*
           * Chap chiziq — bandlar qaysi guruhga tegishli ekanini ko'rsatadi.
           * Nishon kengligiga (`ml-4.5` = padding 10px + nishon 18px ning
           * yarmi) tekislangan: ro'yxat sarlavha matni ostidan boshlanadi,
           * nishondan emas.
           */
          className="mt-0.5 ml-4.5 flex flex-col gap-px border-l border-chrome-line pl-2"
        >
          {group.items.map((item) => (
            <li key={item.id}>
              <Link
                // Kast YO'Q: `MenuRoute` — ro'yxatga olingan marshrutlar
                // literal birlashmasi, xato manzil kompilyatsiyada yiqiladi.
                // `filterMenu` tayyor bo'lmagan bandlarni allaqachon chiqargan.
                to={item.route!}
                /*
                 * ╔══════════════════════════════════════════════════════╗
                 * ║ BAND — MENYUDAGI ENG XIRA YOZUV EDI, ENDI EMAS       ║
                 * ║                                                      ║
                 * ║ Bu yerda `text-sm text-chrome-muted` turardi, guruh  ║
                 * ║ sarlavhasida esa `text-base text-chrome-ink`. Ya'ni  ║
                 * ║ ierarxiya TESKARI edi: kuniga o'nlab marta           ║
                 * ║ BOSILADIGAN element (band — ekranga olib boradi)     ║
                 * ║ panelning eng kichik va eng xira yozuvi bo'lib,      ║
                 * ║ faqat ochib-yopadigan sarlavha esa eng yorug'i.      ║
                 * ║                                                      ║
                 * ║ Sarlavhani xiralashtirish RAD ETILDI: guruhlar       ║
                 * ║ yig'ilgan turadi (`filterMenu` izohi), ya'ni odatda  ║
                 * ║ ekranda FAQAT sarlavhalar ko'rinadi — ular           ║
                 * ║ xiralashsa butun menyu «o'chirilgan» bo'lib qolardi. ║
                 * ║                                                      ║
                 * ║ Shuning uchun ko'tarilgani BAND: 14 → 15 px va rang  ║
                 * ║ to'liq kontrastda. Ierarxiya yo'qolmadi, u endi      ║
                 * ║ O'LCHAM bilan beriladi (16 va 15 px), rang bilan     ║
                 * ║ emas — hover va faol holat esa fon bilan ishlaydi.   ║
                 * ╚══════════════════════════════════════════════════════╝
                 */
                className="relative block rounded-lg px-3 py-2 text-[0.9375rem] text-chrome-ink transition-colors motion-reduce:transition-none hover:bg-chrome-2"
                /*
                 * Faol bandda fon + rang + CHAP CHIZIQ.
                 *
                 * Chiziq (`before:`) ro'yxatning kulrang chegarasi USTIGA
                 * tushadi va uni brend rangiga bo'yaydi: shunda ko'z uzun
                 * ro'yxatda faol bandni fon rangini o'qimasdan, chekkadagi
                 * chiziq bo'yicha topadi. Bu ayniqsa 9 bandli guruhda
                 * seziladi — och fon bir qarashda sezilmasligi mumkin.
                 */
                activeProps={{
                  className:
                    'bg-chrome-2! text-white! font-medium before:absolute before:-left-[9px] before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-chrome-accent',
                }}
                // Bosh sahifa BARCHA manzillarning prefiksi — `exact`siz u
                // doim «faol» bo'lib turardi. Qolgan bandlarda esa `exact`
                // KERAK EMAS: `/products/15` ochilganda «Mahsulotlar» bandi
                // yoritilgan qolishi kerak.
                activeOptions={{ exact: item.route === '/' }}
              >
                {t(item.labelKey)}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
